import { useEffect, useState } from "react";
import { api, inr } from "../../lib/api";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Edit, Trash2, Tag, Zap, Copy, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  code: "",
  kind: "coupon",              // coupon | flash_sale
  title: "",
  description: "",
  banner_message: "",
  discount_type: "percentage", // percentage | flat | free_shipping
  value: 10,
  min_order: 0,
  max_discount: "",
  usage_limit: "",
  per_user_limit: "",
  starts_at: "",
  expires_at: "",
  is_active: true,
  product_ids: [],
  category_slugs: [],
  first_time_only: false,
  allowed_emails: "",
  stackable: false,
};

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const toIso = (local) => {
  if (!local) return null;
  const d = new Date(local);
  return isNaN(d) ? null : d.toISOString();
};

export default function AdminPromotions() {
  const [promos, setPromos] = useState([]);
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("all"); // all | coupon | flash_sale

  const load = () =>
    api.get("/admin/promotions").then((r) => setPromos(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = (kind = "coupon") => {
    setEditing(null);
    setForm({ ...EMPTY, kind });
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p.promotion_id);
    setForm({
      ...EMPTY,
      ...p,
      max_discount: p.max_discount ?? "",
      usage_limit: p.usage_limit ?? "",
      per_user_limit: p.per_user_limit ?? "",
      starts_at: toLocalInput(p.starts_at),
      expires_at: toLocalInput(p.expires_at),
      allowed_emails: (p.allowed_emails || []).join(", "),
      product_ids: p.product_ids || [],
      category_slugs: p.category_slugs || [],
    });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      code: (form.code || "").trim().toUpperCase(),
      kind: form.kind,
      title: form.title.trim(),
      description: form.description || "",
      banner_message: form.banner_message || "",
      discount_type: form.discount_type,
      value: Number(form.value) || 0,
      min_order: Number(form.min_order) || 0,
      max_discount: form.max_discount === "" ? null : Number(form.max_discount),
      usage_limit: form.usage_limit === "" ? null : Number(form.usage_limit),
      per_user_limit: form.per_user_limit === "" ? null : Number(form.per_user_limit),
      starts_at: toIso(form.starts_at),
      expires_at: toIso(form.expires_at),
      is_active: !!form.is_active,
      product_ids: form.product_ids || [],
      category_slugs: form.category_slugs || [],
      first_time_only: !!form.first_time_only,
      allowed_emails: (form.allowed_emails || "")
        .split(",").map((s) => s.trim()).filter(Boolean),
      stackable: !!form.stackable,
    };
    if (!payload.code) return toast.error("Code is required");
    if (!payload.title) return toast.error("Title is required");
    try {
      if (editing) await api.put(`/admin/promotions/${editing}`, payload);
      else await api.post("/admin/promotions", payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const toggle = async (p) => {
    await api.put(`/admin/promotions/${p.promotion_id}`, { ...p, is_active: !p.is_active });
    load();
  };

  const del = async (p) => {
    if (!window.confirm(`Delete promotion "${p.title}"? Usage history is kept.`)) return;
    await api.delete(`/admin/promotions/${p.promotion_id}`);
    toast.success("Deleted");
    load();
  };

  const filtered = promos.filter((p) => tab === "all" || p.kind === tab);
  const totalRedemptions = promos.reduce((s, p) => s + (p.usage_count || 0), 0);
  const activeCount = promos.filter((p) => p.is_active).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-outfit font-bold">Promotions</h1>
          <div className="text-sm text-stone-500">
            {activeCount} active · {totalRedemptions} redemptions · {promos.length} total
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => openNew("coupon")}
            data-testid="new-coupon-btn"
            className="bg-[#3B2F2F] text-white rounded-md px-4 py-2 flex items-center gap-2 hover:bg-black">
            <Tag className="w-4 h-4" /> New coupon
          </button>
          <button
            onClick={() => openNew("flash_sale")}
            data-testid="new-flash-btn"
            className="bg-[#D4AF37] text-[#3B2F2F] rounded-md px-4 py-2 flex items-center gap-2 hover:bg-[#c19f2c] font-semibold">
            <Zap className="w-4 h-4" /> New flash sale
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        {[
          { k: "all", label: "All" },
          { k: "coupon", label: "Coupons" },
          { k: "flash_sale", label: "Flash sales" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`promo-tab-${t.k}`}
            className={`text-sm rounded-full px-4 py-1.5 border transition ${
              tab === t.k
                ? "bg-[#3B2F2F] text-white border-[#3B2F2F]"
                : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead>Window</TableHead>
              <TableHead>Usage</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-stone-500 py-10">
                  No promotions yet. Create your first coupon or flash sale to get started.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((p) => (
              <TableRow key={p.promotion_id} data-testid={`promo-row-${p.code}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="font-outfit font-bold text-[#3B2F2F]">{p.code}</span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(p.code);
                        toast.success("Copied");
                      }}
                      className="text-stone-400 hover:text-[#3B2F2F]">
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5">{p.title}</div>
                </TableCell>
                <TableCell>
                  {p.kind === "flash_sale" ? (
                    <Badge className="bg-[#D4AF37] text-[#3B2F2F]">
                      <Zap className="w-3 h-3 mr-1" /> Flash sale
                    </Badge>
                  ) : (
                    <Badge variant="secondary">Coupon</Badge>
                  )}
                </TableCell>
                <TableCell className="font-outfit font-semibold">
                  {p.discount_type === "percentage" && `${p.value}% off`}
                  {p.discount_type === "flat" && inr(p.value)}
                  {p.discount_type === "free_shipping" && "Free shipping"}
                  {p.min_order > 0 && (
                    <div className="text-xs text-stone-500 mt-0.5">
                      Min {inr(p.min_order)}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-xs text-stone-600">
                  {p.starts_at && (
                    <div>From {new Date(p.starts_at).toLocaleDateString()}</div>
                  )}
                  {p.expires_at ? (
                    <div>Until {new Date(p.expires_at).toLocaleDateString()}</div>
                  ) : (
                    !p.starts_at && <span className="text-stone-400">No end</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 text-sm">
                    <TrendingUp className="w-3 h-3 text-[#2E4A35]" />
                    <span className="font-outfit font-semibold">{p.usage_count || 0}</span>
                    {p.usage_limit && (
                      <span className="text-xs text-stone-500">/ {p.usage_limit}</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <button
                    onClick={() => toggle(p)}
                    data-testid={`promo-toggle-${p.code}`}
                    className={`text-xs rounded-full px-3 py-1 ${
                      p.is_active
                        ? "bg-[#E8EDDF] text-[#2E4A35]"
                        : "bg-stone-100 text-stone-500"
                    }`}>
                    {p.is_active ? "Active" : "Inactive"}
                  </button>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(p)} className="text-stone-500 hover:text-[#3B2F2F]" data-testid={`promo-edit-${p.code}`}>
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => del(p)} className="text-stone-500 hover:text-red-600" data-testid={`promo-del-${p.code}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit" : "New"} {form.kind === "flash_sale" ? "flash sale" : "coupon"}
            </DialogTitle>
            <DialogDescription>
              Configure discount rules, product restrictions and validity window. All fields respect the constraints at checkout.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Code (e.g. SAVE20)">
                <Input value={form.code} onChange={(e) => upd("code", e.target.value.toUpperCase())} data-testid="promo-code" placeholder="WELCOME10" />
              </Field>
              <Field label="Type">
                <Select value={form.kind} onValueChange={(v) => upd("kind", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="coupon">Coupon (manual entry)</SelectItem>
                    <SelectItem value="flash_sale">Flash sale (shown as banner)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <div className="col-span-2">
                <Field label="Title">
                  <Input value={form.title} onChange={(e) => upd("title", e.target.value)} data-testid="promo-title" placeholder="Weekend flash — 25% off" />
                </Field>
              </div>
              <div className="col-span-2">
                <Field label="Description (internal)">
                  <Textarea value={form.description} onChange={(e) => upd("description", e.target.value)} placeholder="Any notes for your team" />
                </Field>
              </div>
              {form.kind === "flash_sale" && (
                <div className="col-span-2">
                  <Field label="Banner message (shown on homepage)">
                    <Input value={form.banner_message} onChange={(e) => upd("banner_message", e.target.value)} data-testid="promo-banner" placeholder="Weekend Flash — 25% off. Ends Sun midnight." />
                  </Field>
                </div>
              )}
            </div>

            <div className="border-t pt-4">
              <div className="font-outfit font-semibold mb-3">Discount</div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Discount type">
                  <Select value={form.discount_type} onValueChange={(v) => upd("discount_type", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage off</SelectItem>
                      <SelectItem value="flat">Flat amount off</SelectItem>
                      <SelectItem value="free_shipping">Free shipping</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={form.discount_type === "percentage" ? "Percent (%)" : "Amount (₹)"}>
                  <Input type="number" value={form.value} onChange={(e) => upd("value", e.target.value)} disabled={form.discount_type === "free_shipping"} data-testid="promo-value" />
                </Field>
                <Field label="Max discount cap (₹, optional)">
                  <Input type="number" value={form.max_discount} onChange={(e) => upd("max_discount", e.target.value)} disabled={form.discount_type !== "percentage"} placeholder="No cap" />
                </Field>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="font-outfit font-semibold mb-3">Constraints</div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Min order value (₹)">
                  <Input type="number" value={form.min_order} onChange={(e) => upd("min_order", e.target.value)} />
                </Field>
                <Field label="Total usage limit">
                  <Input type="number" value={form.usage_limit} onChange={(e) => upd("usage_limit", e.target.value)} placeholder="Unlimited" />
                </Field>
                <Field label="Per-user limit">
                  <Input type="number" value={form.per_user_limit} onChange={(e) => upd("per_user_limit", e.target.value)} placeholder="Unlimited" />
                </Field>
                <Field label="Starts at (optional)">
                  <Input type="datetime-local" value={form.starts_at} onChange={(e) => upd("starts_at", e.target.value)} />
                </Field>
                <Field label="Expires at (optional)">
                  <Input type="datetime-local" value={form.expires_at} onChange={(e) => upd("expires_at", e.target.value)} data-testid="promo-expires" />
                </Field>
                <div className="flex flex-col gap-2 text-sm justify-end pb-2">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={form.first_time_only} onChange={(e) => upd("first_time_only", e.target.checked)} />
                    First-time customers only
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={form.is_active} onChange={(e) => upd("is_active", e.target.checked)} data-testid="promo-active" />
                    Active
                  </label>
                </div>
              </div>

              <div className="mt-4">
                <Field label="Allowed emails (comma-separated, leave blank for anyone)">
                  <Input value={form.allowed_emails} onChange={(e) => upd("allowed_emails", e.target.value)} placeholder="vip@x.com, gold@y.com" />
                </Field>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="font-outfit font-semibold mb-3">Product / category restriction (leave empty = all)</div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1.5">Categories</div>
                  <div className="max-h-40 overflow-auto border border-stone-200 rounded p-2 space-y-1">
                    {cats.map((c) => (
                      <label key={c.slug} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={form.category_slugs.includes(c.slug)}
                          onChange={(e) => {
                            const set = new Set(form.category_slugs);
                            if (e.target.checked) set.add(c.slug); else set.delete(c.slug);
                            upd("category_slugs", Array.from(set));
                          }}
                        /> {c.name}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1.5">Products</div>
                  <div className="max-h-40 overflow-auto border border-stone-200 rounded p-2 space-y-1">
                    {products.map((p) => (
                      <label key={p.product_id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={form.product_ids.includes(p.product_id)}
                          onChange={(e) => {
                            const set = new Set(form.product_ids);
                            if (e.target.checked) set.add(p.product_id); else set.delete(p.product_id);
                            upd("product_ids", Array.from(set));
                          }}
                        /> {p.name}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={save}
              data-testid="save-promo-btn"
              className="w-full bg-[#3B2F2F] text-white rounded-md py-2.5 font-medium hover:bg-black">
              {editing ? "Update promotion" : "Create promotion"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-outfit font-semibold mb-1.5">
        {label}
      </div>
      {children}
    </label>
  );
}
